class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.setupCartUpdateListener();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setupCartUpdateListener() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.initCartDrawerSwiper();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);

        // Initialize Swiper after cart drawer is fully opened
        this.initCartDrawerSwiper();
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();

      this.initCartDrawerSwiper();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initCartDrawerSwiper() {
    // Check if Swiper is available
    if (typeof Swiper === "undefined") {
      console.warn("Swiper is not loaded yet");
      return;
    }

    const swiperElement = this.querySelector(
      ".footer-upsell__products-list.swiper"
    );
    if (swiperElement && !swiperElement.swiper) {
      try {
        const swiper = new Swiper(swiperElement, {
          slidesPerView: 1.2,
          spaceBetween: 12,
          loop: false,
          breakpoints: {
            749: {
              slidesPerView: 1.4,
              spaceBetween: 16,
            },
          },
          mousewheel: {
            forceToAxis: true,
          }
        });
      } catch (error) {
        console.error("Error initializing cart drawer Swiper:", error);
      }
    }
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.throttledSellingPlanClick = throttle(
      this.handleSellingPlanClick.bind(this),
      1000
    );
    this.handleOutsideClick = this.handleOutsideClick.bind(this);

    this.dropdowns = this.querySelectorAll(".cart-subscription-dropdown");
    if (this.dropdowns?.length) {
      this.dropdowns.forEach(this.initDropdown.bind(this));
    }
    this.activeDropdown = null;

    document.addEventListener("click", this.handleOutsideClick);
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  initDropdown(dropdown) {
    if (!dropdown) return;

    const trigger = dropdown.querySelector(
      ".cart-subscription-dropdown-trigger"
    );
    const itemsWrapper = dropdown.querySelector(
      ".cart-subscription-dropdown-items-wrapper"
    );
    const items = dropdown.querySelector(".cart-subscription-dropdown-items");
    const sellingPlanQty = dropdown.dataset.sellingPlanQty || 1;

    const props = {
      trigger,
      items,
      itemsWrapper,
      fullHeight: items.offsetHeight,
      isOpen: false,
      sellingPlanQty: sellingPlanQty,
      _onTriggerClick: (event) => this.toggleDropdown(event, dropdown),
      _onOptionClick: (event) =>
        this.throttledSellingPlanClick(event, dropdown),
    };

    Object.assign(dropdown, props);

    trigger.addEventListener("click", props._onTriggerClick);
    items.addEventListener("click", props._onOptionClick);
  }

  toggleDropdown(event, dropdown) {
    event.preventDefault();
    event.stopPropagation();
    if (dropdown.classList.contains("disabled")) return;

    dropdown.isOpen
      ? this.closeDropdown(dropdown)
      : this.openDropdown(dropdown);
  }

  openDropdown(dropdown) {
    if (this.activeDropdown && this.activeDropdown !== dropdown) {
      this.closeDropdown(this.activeDropdown);
    }

    dropdown.isOpen = true;
    dropdown.classList.add("active");
    dropdown.itemsWrapper.style.height = `${dropdown.fullHeight}px`;

    this.activeDropdown = dropdown;
  }

  closeDropdown(dropdown) {
    dropdown.isOpen = false;
    dropdown.classList.remove("active");
    dropdown.itemsWrapper.style.height = "0px";

    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    }
  }

  handleSellingPlanClick(e, dropdown) {
    if (e.target.tagName !== "SPAN") return;
    e.preventDefault();
    e.stopPropagation();

    const index = e.target.dataset.index;
    const sellingPlanQty = dropdown.sellingPlanQty || 1;
    const sellingPlanId = e.target.dataset.sellingPlanId;

    const selectedPlanId = e.target.closest(".cart-subscription-dropdown")
      ?.dataset?.selectedPlanId;

    if (selectedPlanId == sellingPlanId) return;
    const cartItems =
      this.closest("cart-items") || this.closest("cart-drawer-items");
    cartItems.updateQuantity(
      index,
      sellingPlanQty,
      "subscription",
      sellingPlanId
    );
  }

  handleOutsideClick(e) {
    if (!this.activeDropdown) return;

    const activeWrapper = this.activeDropdown.querySelector(
      ".cart-subscription-dropdown-items-wrapper"
    );
    if (!activeWrapper.contains(e.target)) {
      this.closeDropdown(this.activeDropdown);
    }
  }

  clearDropdownEvents(dropdown) {
    if (!dropdown) return;

    if (dropdown.trigger && typeof dropdown._onTriggerClick === 'function') {
      dropdown.trigger.removeEventListener("click", dropdown._onOptionClick);
    }
    if (dropdown.items && typeof dropdown._onOptionClick === 'function') {
      dropdown.items.removeEventListener("click", dropdown._onOptionClick);
    }
  }

  disconnectedCallback() {
    this.dropdowns.forEach((dropdown) => {
      this.clearDropdownEvents(dropdown);
    });
    document.removeEventListener("click", this.handleOutsideClick);
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Global function to initialize cart drawer Swiper (can be called from anywhere)
window.initCartDrawerSwiper = function () {
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer && typeof cartDrawer.initCartDrawerSwiper === "function") {
    cartDrawer.initCartDrawerSwiper();
  }
};

if (!customElements.get("subscription-toggle")) {
  customElements.define(
    "subscription-toggle",
    class extends HTMLElement {
      constructor() {
        super();

        this.firstAvailableSellingPlanId = this.dataset.sellingPlanId;
        this.input = this.querySelector('input[name="subscription"]');
        this.isChecked = this.input.checked;
        this.index = this.dataset.index;
        this.sellingPlanQty = this.dataset.sellingPlanQty;

        this.inputChangeHandler = this.inputChangeHandler.bind(this);
        this.debouncedCartUpdate = debounce((checked) => {
          this.updateCartQuantity(checked);
        }, 300);
      }
      connectedCallback() {
        this.input.addEventListener("change", this.inputChangeHandler);
      }

      inputChangeHandler(e) {
        e.preventDefault();
        e.stopPropagation();

        this.debouncedCartUpdate(e.target.checked);
      }

      updateCartQuantity(newChecked) {
        if (this.isChecked == newChecked) return;

        const cartItems =
          this.closest("cart-items") || this.closest("cart-drawer-items");
        cartItems.updateQuantity(
          this.index,
          this.sellingPlanQty || 1,
          "subscription",
          ...(this.input.checked
            ? [this.firstAvailableSellingPlanId]
            : ["clear"])
        );
      }
    }
  );
}
